import { CookieHelper, UUIDHelper, StorageHelper, stripHtml } from './helpers.js';
import { MAX_HISTORY_MESSAGES } from './constants.js';

const LEGACY_HISTORY_KEY = 'chatyx_history';
const CONVERSATIONS_KEY_PREFIX = 'chatyx_conversations';
const LEGACY_COOKIE_NAME = 'chatyx_user_id';

function safeOrigin() {
  try {
    if (typeof location !== 'undefined' && location.hostname) {
      return location.hostname;
    }
  } catch (_) {
    /* sandboxed */
  }
  return 'local';
}

export function buildHistoryStorageKey(botId, origin = safeOrigin()) {
  if (!botId) return LEGACY_HISTORY_KEY;
  return `${LEGACY_HISTORY_KEY}:${origin}:${botId}`;
}

export function buildConversationsIndexKey(botId, origin = safeOrigin()) {
  return `${CONVERSATIONS_KEY_PREFIX}:${origin}:${botId || 'default'}`;
}

export const UserManager = {
  STORAGE_KEY: 'chatyx_cookie_consent',

  getUserData() {
    // One-shot migration: move user data from the old cookie to localStorage.
    const cookieValue = CookieHelper.get(LEGACY_COOKIE_NAME);
    if (cookieValue && !StorageHelper.get(this.STORAGE_KEY)) {
      try {
        StorageHelper.set(this.STORAGE_KEY, JSON.parse(cookieValue));
      } catch (_) {}
      CookieHelper.delete(LEGACY_COOKIE_NAME);
    }

    return StorageHelper.get(this.STORAGE_KEY);
  },

  getUserId() {
    let userData = this.getUserData();
    if (!userData || !userData.user_id) {
      userData = {
        user_id: UUIDHelper.generate(),
        consent_given: false,
        created_at: new Date().toISOString()
      };
      this.saveUserData(userData);
    }
    return userData.user_id;
  },

  getConsent() {
    const userData = this.getUserData();
    return userData ? userData.consent_given === true : false;
  },

  setConsent(value) {
    let userData = this.getUserData();
    if (!userData) {
      userData = {
        user_id: UUIDHelper.generate(),
        consent_given: false,
        created_at: new Date().toISOString()
      };
    }
    userData.consent_given = value;
    userData.consent_date = new Date().toISOString();
    this.saveUserData(userData);
  },

  saveUserData(userData) {
    StorageHelper.set(this.STORAGE_KEY, userData);
  }
};

export function createHistoryManager(botId, origin = safeOrigin(), options = {}) {
  // `storageKey` lets a caller (the conversation manager) scope a bucket to a
  // specific conversation instead of the default per-bot key.
  const STORAGE_KEY = options.storageKey || buildHistoryStorageKey(botId, origin);
  const maxMessages =
    typeof options.maxMessages === 'number' && options.maxMessages > 0 ? options.maxMessages : MAX_HISTORY_MESSAGES;
  // The legacy-global migration only makes sense for the canonical per-bot
  // bucket; conversation sub-buckets opt out so they never adopt stray data.
  const migrateLegacy = options.migrateLegacy !== false;

  function trim(messages) {
    if (!Array.isArray(messages)) return [];
    if (messages.length <= maxMessages) return messages;
    return messages.slice(messages.length - maxMessages);
  }

  // One-shot migration: if the namespaced key is empty but a legacy global
  // history exists, adopt it for this bot then clean up the legacy key.
  if (migrateLegacy && botId && STORAGE_KEY !== LEGACY_HISTORY_KEY) {
    const existing = StorageHelper.get(STORAGE_KEY);
    const legacy = StorageHelper.get(LEGACY_HISTORY_KEY);
    if (!existing && Array.isArray(legacy) && legacy.length > 0) {
      StorageHelper.set(STORAGE_KEY, legacy);
      StorageHelper.remove(LEGACY_HISTORY_KEY);
    }
  }

  return {
    STORAGE_KEY,
    maxMessages,

    load() {
      return StorageHelper.get(STORAGE_KEY) || [];
    },

    save(messages) {
      StorageHelper.set(STORAGE_KEY, trim(messages));
    },

    addMessage(message) {
      const history = this.load();
      history.push({
        ...message,
        timestamp: message.timestamp || new Date().toISOString()
      });
      this.save(history);
    },

    markUserMessagesAsSeen() {
      const history = this.load();
      let changed = false;
      const updated = history.map((msg) => {
        if (msg.type === 'user' && !msg.seen) {
          changed = true;
          return { ...msg, seen: true };
        }
        return msg;
      });
      if (changed) this.save(updated);
    },

    clear() {
      StorageHelper.remove(STORAGE_KEY);
    }
  };
}

// In-memory history manager used for ephemeral sessions such as the dashboard
// live preview, where test conversations must never touch localStorage nor
// pollute a real visitor's history. Mirrors the createHistoryManager interface.
export function createMemoryHistoryManager(options = {}) {
  const maxMessages =
    typeof options.maxMessages === 'number' && options.maxMessages > 0 ? options.maxMessages : MAX_HISTORY_MESSAGES;
  let messages = [];

  function trim(list) {
    if (!Array.isArray(list)) return [];
    if (list.length <= maxMessages) return list;
    return list.slice(list.length - maxMessages);
  }

  return {
    STORAGE_KEY: null,
    maxMessages,

    load() {
      return messages.slice();
    },

    save(list) {
      messages = trim(Array.isArray(list) ? list.slice() : []);
    },

    addMessage(message) {
      messages.push({
        ...message,
        timestamp: message.timestamp || new Date().toISOString()
      });
      messages = trim(messages);
    },

    markUserMessagesAsSeen() {
      messages = messages.map((msg) => (msg.type === 'user' && !msg.seen ? { ...msg, seen: true } : msg));
    },

    clear() {
      messages = [];
    }
  };
}

// Multi-conversation manager. Layers a list of named conversations on top of
// the per-bucket history managers: each conversation owns its own message
// bucket, plus a lightweight index (active id + metadata for the list view).
//
// It also satisfies the history-manager interface (load/save/addMessage/
// markUserMessagesAsSeen/clear) by delegating to the *active* conversation, so
// existing widget/renderer code keeps working unchanged — calls simply land in
// whichever conversation is currently selected.
export function createConversationManager(botId, origin = safeOrigin(), options = {}) {
  const ephemeral = options.ephemeral === true;
  const maxMessages =
    typeof options.maxMessages === 'number' && options.maxMessages > 0 ? options.maxMessages : MAX_HISTORY_MESSAGES;
  const INDEX_KEY = buildConversationsIndexKey(botId, origin);
  // The first/legacy conversation reuses the canonical per-bot bucket so an
  // existing visitor's history is adopted seamlessly; later conversations get
  // a suffixed key derived from it.
  const baseKey = buildHistoryStorageKey(botId, origin);

  let memIndex = null; // in-memory index, used in ephemeral mode
  const managerCache = new Map();

  function readIndex() {
    if (ephemeral) return memIndex;
    return StorageHelper.get(INDEX_KEY);
  }

  function writeIndex(index) {
    if (ephemeral) {
      memIndex = index;
      return;
    }
    StorageHelper.set(INDEX_KEY, index);
  }

  function makeManager(key) {
    if (ephemeral) return createMemoryHistoryManager({ maxMessages });
    return createHistoryManager(botId, origin, { maxMessages, storageKey: key, migrateLegacy: false });
  }

  function managerFor(conv) {
    if (!managerCache.has(conv.id)) {
      managerCache.set(conv.id, makeManager(conv.key));
    }
    return managerCache.get(conv.id);
  }

  function createConversationMeta() {
    const id = UUIDHelper.generate();
    const now = new Date().toISOString();
    return { id, key: `${baseKey}:${id}`, createdAt: now, updatedAt: now, preview: '' };
  }

  function createInitialIndex() {
    const id = UUIDHelper.generate();
    const now = new Date().toISOString();
    let preview = '';
    let updatedAt = now;
    // Persistent only: adopt an existing legacy bucket as the first conversation.
    if (!ephemeral) {
      const legacy = StorageHelper.get(baseKey);
      if (Array.isArray(legacy) && legacy.length > 0) {
        const last = legacy[legacy.length - 1];
        preview = stripHtml(last && last.content);
        updatedAt = (last && last.timestamp) || now;
      }
    }
    return {
      activeId: id,
      conversations: [{ id, key: baseKey, createdAt: now, updatedAt, preview }]
    };
  }

  function ensureIndex() {
    let index = readIndex();
    if (!index || !Array.isArray(index.conversations) || index.conversations.length === 0) {
      index = createInitialIndex();
      writeIndex(index);
    } else if (!index.conversations.some((c) => c.id === index.activeId)) {
      index.activeId = index.conversations[0].id;
      writeIndex(index);
    }
    return index;
  }

  function getActiveConv() {
    const index = ensureIndex();
    return index.conversations.find((c) => c.id === index.activeId) || index.conversations[0];
  }

  function activeManager() {
    return managerFor(getActiveConv());
  }

  // Refresh the active conversation's preview/updatedAt metadata after a
  // message change so the conversations list stays in sync.
  function touchActive(messages) {
    const index = ensureIndex();
    const conv = index.conversations.find((c) => c.id === index.activeId);
    if (!conv) return;
    const last = Array.isArray(messages) && messages.length ? messages[messages.length - 1] : null;
    conv.updatedAt = (last && last.timestamp) || new Date().toISOString();
    conv.preview = last ? stripHtml(last.content) : '';
    writeIndex(index);
  }

  // Prime storage on creation so the index exists from the first interaction.
  ensureIndex();

  return {
    get STORAGE_KEY() {
      return getActiveConv().key;
    },
    maxMessages,

    // ----- history-manager interface (delegates to active conversation) -----
    load() {
      return activeManager().load();
    },

    save(messages) {
      const mgr = activeManager();
      mgr.save(messages);
      touchActive(mgr.load());
    },

    addMessage(message) {
      const mgr = activeManager();
      mgr.addMessage(message);
      touchActive(mgr.load());
    },

    markUserMessagesAsSeen() {
      activeManager().markUserMessagesAsSeen();
    },

    clear() {
      activeManager().clear();
      touchActive([]);
    },

    // ----- conversation management -----
    listConversations() {
      const index = ensureIndex();
      return index.conversations
        .map((c) => ({
          id: c.id,
          preview: c.preview || '',
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          active: c.id === index.activeId
        }))
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    },

    getActiveId() {
      return ensureIndex().activeId;
    },

    switchTo(id) {
      const index = ensureIndex();
      if (!index.conversations.some((c) => c.id === id)) return false;
      index.activeId = id;
      writeIndex(index);
      return true;
    },

    // Reuse an existing empty conversation rather than piling up blank ones:
    // if the active conversation has no messages, just keep it.
    createConversation() {
      const index = ensureIndex();
      const active = index.conversations.find((c) => c.id === index.activeId);
      if (active && managerFor(active).load().length === 0) {
        return active.id;
      }
      const meta = createConversationMeta();
      index.conversations.push(meta);
      index.activeId = meta.id;
      writeIndex(index);
      return meta.id;
    },

    deleteConversation(id) {
      const index = ensureIndex();
      const idx = index.conversations.findIndex((c) => c.id === id);
      if (idx === -1) return false;
      const [removed] = index.conversations.splice(idx, 1);
      managerCache.delete(removed.id);
      if (!ephemeral && removed.key) StorageHelper.remove(removed.key);
      if (index.conversations.length === 0) {
        const fresh = createInitialIndex();
        writeIndex(fresh);
        return true;
      }
      if (index.activeId === id) {
        index.activeId = index.conversations[0].id;
      }
      writeIndex(index);
      return true;
    }
  };
}

// Backwards-compatible default export: no namespacing. Prefer
// createHistoryManager(botId) when a botId is available.
export const HistoryManager = createHistoryManager(null);
